import { useState, type FormEvent } from "react";
import { categories } from "@/mocks/categories";

const SUBMIT_URL = "https://readdy.ai/api/form/dahhon3oh653ivfvnvtg";

type Status = "idle" | "submitting" | "success" | "error";

const asRecord = (v: unknown): Record<string, unknown> | null =>
  typeof v === "object" && v !== null ? (v as Record<string, unknown>) : null;

const pick = (v: unknown): string => (typeof v === "string" ? v : "");

export default function SubscribeCta() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const fd = new FormData(form);

    const honeypot = pick(fd.get("website_alt")).trim();
    if (honeypot) {
      setStatus("success");
      return;
    }

    setStatus("submitting");
    setFormError("");

    const payload = new URLSearchParams();
    fd.forEach((value, key) => {
      if (key === "website_alt") return;
      if (typeof value === "string" && value.trim() !== "") payload.append(key, value);
    });

    try {
      const response = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = asRecord(JSON.parse(responseText));
      } catch {
        parsed = null;
      }
      const meta = asRecord(parsed?.meta);
      const serverMsg =
        pick(meta?.message) || pick(parsed?.message) || pick(meta?.detail) || responseText;
      const isSpam = serverMsg.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后再试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，请稍后再试。");
    }
  };

  return (
    <section className="bg-primary-500 px-4 md:px-8 py-14 md:py-20">
      <div className="grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-6">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-foreground-950/20 text-background-50 text-xs font-medium">
            <i className="ri-notification-3-line" />
            栏目订阅
          </span>
          <h2 className="mt-5 font-heading font-black text-2xl md:text-4xl text-background-50 leading-tight">
            订阅「黑料今日日报」
            <br />
            不错过任何一条新瓜
          </h2>
          <p className="mt-4 text-sm md:text-base text-background-100 max-w-xl leading-relaxed">
            填写邮箱并勾选你关心的栏目，日报更新后我们会在同一时间把精选汇总送到你的收件箱。
            随时可以更换关注的栏目。
          </p>
          <ul className="mt-6 space-y-2.5">
            {["每日 09:00 更新一期", "栏目可自由勾选，随时调整", "只发精选汇总，不做无意义推送"].map(
              (t) => (
                <li key={t} className="flex items-center gap-2 text-sm text-background-100">
                  <i className="ri-checkbox-circle-line text-background-50" />
                  {t}
                </li>
              ),
            )}
          </ul>
        </div>

        <div className="lg:col-span-6">
          <form
            id="daily-subscribe-form"
            data-readdy-form
            onSubmit={handleSubmit}
            className="rounded-lg bg-background-50 p-5 md:p-7"
            noValidate
          >
            <div className="flex flex-col gap-2">
              <label htmlFor="sub-email" className="text-sm font-medium text-foreground-800">
                邮箱地址
              </label>
              <input
                id="sub-email"
                type="email"
                name="email"
                required
                placeholder="you@example.com"
                className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
              />
            </div>

            <fieldset className="mt-5">
              <legend className="text-sm font-medium text-foreground-800">关注的栏目</legend>
              <div className="mt-3 flex flex-wrap gap-2">
                {categories.map((cat) => (
                  <label
                    key={cat.id}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-background-300 text-xs text-foreground-700 cursor-pointer hover:border-primary-300 hover:bg-primary-50 transition-colors has-[:checked]:bg-primary-100 has-[:checked]:border-primary-400 has-[:checked]:text-primary-800"
                  >
                    <input type="checkbox" name="columns" value={cat.name} className="accent-primary-500" />
                    {cat.name}
                  </label>
                ))}
              </div>
            </fieldset>

            <div className="mt-5 flex flex-col gap-2">
              <label htmlFor="sub-note" className="text-sm font-medium text-foreground-800">
                想说的话（选填）
              </label>
              <textarea
                id="sub-note"
                name="message"
                rows={3}
                maxLength={500}
                placeholder="例如：希望多更新影视剧透栏目"
                className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 resize-none"
              />
            </div>

            <input
              type="text"
              name="website_alt"
              className="form-alt-note"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
            />

            <button
              type="submit"
              disabled={status === "submitting"}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {status === "submitting" ? "提交中…" : "提交订阅"}
              <i className="ri-send-plane-line" />
            </button>

            {status === "success" && (
              <p className="mt-3 text-sm text-primary-700 flex items-center gap-1.5">
                <i className="ri-checkbox-circle-fill" />
                订阅成功，下一期日报更新时会发送到你的邮箱。
              </p>
            )}
            {status === "error" && (
              <p className="mt-3 text-sm text-primary-800 flex items-center gap-1.5">
                <i className="ri-error-warning-fill" />
                {formError}
              </p>
            )}
            <p className="mt-3 text-xs text-foreground-500">
              提交即表示同意我们仅将邮箱用于日报推送，不含任何第三方营销。
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}